import { ArrowDownIcon, ArrowLeftIcon } from 'internalLibrary';
import { FC, useMemo } from 'react';
import { formatNumberToLocale } from '../../formatters/formatNumberToLocale';
import { LogoSquareImage } from 'assets';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import DesktopProductProperty from './DesktopProductProperty/DesktopProductProperty';
import InteractiveArea from 'ui/components/InteractiveArea';
import ProgressBar from './ProgressBar/ProgressBar';
import styles from './Navbar.module.scss';
import useLoanOffer from 'ui/components/LoanApplicationProcessLayout/Sidebar/useLoanOffer';

interface NavbarProps {
  progress: number;
  isDropdownOpen: boolean;
  onArrowDownClick: () => void;
  onBackClick?: () => void;
}

export const Navbar: FC<NavbarProps> = ({
  progress,
  isDropdownOpen,
  onArrowDownClick,
  onBackClick,
}) => {
  const { t } = useTranslation();
  const { loanOffer } = useLoanOffer();

  const { amount, term, agreementEndDate } = useMemo(() => {
    return {
      amount: formatNumberToLocale(loanOffer?.totalPrincipal) || '####',
      term: loanOffer?.term || '##',
      agreementEndDate: loanOffer?.agreementEndDate || `##-##-##`,
    };
  }, [loanOffer?.totalPrincipal, loanOffer?.term, loanOffer?.agreementEndDate]);

  const hideArrowDown = progress > 0.33;

  return (
    <div
      className={classNames(
        styles.container,
        isDropdownOpen && styles['opened'],
      )}
    >
      <div className={styles['container-header']}>
        {onBackClick && <ArrowLeftIcon onClick={onBackClick} />}
        <div className={styles['container-slider']}>
          <ProgressBar className={styles.slider} progress={progress} />
        </div>
        <LogoSquareImage />
      </div>
      <hr className={styles.hr} />
      <div className={styles['wrapper-subheader']}>
        <div className={styles['container-subheader']}>
          <div
            className={styles['container-subheader-element']}
            key={'registrationHeader-0'}
          >
            <DesktopProductProperty
              value={amount}
              valueSuffix={t('currencySymbol')}
              label={t('navbar_amount')}
              loading={!loanOffer?.totalPrincipal}
            />
          </div>

          <div
            className={styles['container-subheader-element']}
            key={'registrationHeader-1'}
          >
            <SubHeaderSeparation />
            <DesktopProductProperty
              value={term}
              valueSuffix={t('days')}
              label={t('navbar_duration')}
              loading={!loanOffer?.term}
            />
          </div>

          <div
            className={styles['container-subheader-element']}
            key={'registrationHeader-2'}
          >
            <SubHeaderSeparation />
            <DesktopProductProperty
              value={agreementEndDate}
              label={t('navbar_repaymentDate')}
              loading={!loanOffer?.agreementEndDate}
            />
          </div>
        </div>
        {!hideArrowDown && (
          <InteractiveArea className={styles.icon} onClick={onArrowDownClick}>
            <ArrowDownIcon />
          </InteractiveArea>
        )}
      </div>
    </div>
  );
};

const SubHeaderSeparation = () => <hr className={styles.vr} />;
